import React, { useEffect, useState } from 'react';
import { DemiLightText } from '@/lib/Text';
import { formatAmountText, stripAmountComma } from '@/lib/utils';
import { useAmountInputCaret } from '@/lib/amountInputCaret';

/**
 * 비워 둘 수 있는 숫자 칸(기본 정산의 재등록·노쇼 값).
 *
 * 공용 NumberTextInput 은 빈칸을 0 으로 알린다. 여기서는 「비움」(= 신규·출석 값을 쓴다)과 「0」(= 0원·0%)이
 * 다른 뜻이라 빈칸을 null 로 알려야 한다. 그래서 칸 하나만 따로 둔다.
 *
 * 입력 규칙은 같은 줄의 신규 칸(NumberTextInput)과 똑같이 맞춘다 — 칸마다 같은 글자를 다르게 받으면 안 된다.
 * - 원: 콤마 모드(useAmountInputCaret). 한 번에 들어온 값(붙여넣기·자동완성)은 정규형만 받는다(「1,5」 거부).
 * - %: 숫자와 소수점만(/^\d*\.?\d*$/). 콤마는 아예 받지 않는다(「1,5」 가 15% 가 되지 않게).
 */
type Props = {
    value: number | null;
    onChange: (value: number | null) => void;
    unit: '원' | '%';
    placeholder?: string;
    disabled?: boolean;
    ariaLabel?: string;
};

const PERCENT_PATTERN = /^\d*\.?\d*$/;

const display = (value: number | null, unit: Props['unit']) => {
    if (value === null || value === undefined) return '';
    return unit === '원' ? formatAmountText(String(value)) : String(value);
};

const toValue = (digits: string): number | null => (digits === '' || digits === '.' ? null : Number(digits));

const OptionalValueInput = ({ value, onChange, unit, placeholder, disabled, ariaLabel }: Props) => {
    const [focused, setFocused] = useState(false);
    const [text, setText] = useState(() => display(value, unit));
    const { changeAmount, dropPendingCaret, pasteAmount, dropAmount } = useAmountInputCaret();
    const isWon = unit === '원';

    // 부모가 다른 값으로 바꿨을 때만 맞춘다("40." 같은 입력 중 표기를 덮지 않게).
    useEffect(() => {
        setText((current) => {
            if (toValue(stripAmountComma(current)) === value) return current;
            dropPendingCaret();
            return display(value, unit);
        });
    }, [value, unit, dropPendingCaret]);

    return (
        <div
            className={`flex items-center h-[43px] border border-solid rounded-[10px] px-[18px] ${
                disabled ? 'bg-gray-100' : 'bg-white'
            } ${focused ? 'border-primary' : 'border-[#D9D9D9B2]'}`}
        >
            <input
                type="text"
                aria-label={ariaLabel}
                inputMode={isWon ? 'numeric' : 'decimal'}
                value={text}
                disabled={disabled}
                placeholder={placeholder}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    setFocused(false);
                    // "05"·"40." 같은 표기는 칸을 벗어날 때 정리한다(입력 중에 고치면 커서가 튄다).
                    setText((current) => display(toValue(stripAmountComma(current)), unit));
                }}
                onPaste={isWon ? pasteAmount : undefined}
                onDrop={isWon ? dropAmount : undefined}
                onChange={(e) => {
                    if (isWon) {
                        const digits = changeAmount(e, stripAmountComma(text));
                        if (digits === null) return;
                        setText(formatAmountText(digits));
                        onChange(toValue(digits));
                        return;
                    }
                    const raw = e.target.value;
                    if (!PERCENT_PATTERN.test(raw)) return;
                    setText(raw);
                    onChange(toValue(raw));
                }}
                className="flex flex-grow outline-none w-[60px] text-[16px] leading-[23px] text-[#3D4244] bg-transparent placeholder:text-[#9CA3AF]"
            />
            <DemiLightText className="flex">{unit}</DemiLightText>
        </div>
    );
};

export default OptionalValueInput;
